import { MISSION_COPPER_INK } from "@/lib/mission-drive";
import { getMissionDrive, highlightFromElement, subscribeMissionDrive } from "@/lib/mission-drive";
import { autoUpdate, flip, offset, shift, useFloating } from "@floating-ui/react-dom";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

const OUTLINE = "#C45C26";
const FILL = "rgba(196,92,38,.10)";

type Box = NonNullable<ReturnType<typeof getMissionDrive>["highlight"]>;

function sameBox(a: Box | null, b: Box | null): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    a.top === b.top &&
    a.left === b.left &&
    a.width === b.width &&
    a.height === b.height &&
    a.label === b.label &&
    a.missionId === b.missionId &&
    a.testid === b.testid
  );
}

function boxRect(box: Box): DOMRect {
  return new DOMRect(box.left, box.top, box.width, box.height);
}

export function TargetHighlight() {
  const [box, setBox] = useState(() => getMissionDrive().highlight);
  const { refs, floatingStyles } = useFloating({
    placement: "right-start",
    middleware: [offset(8), flip({ fallbackPlacements: ["top-start", "bottom-start", "left-start"] }), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
  });

  useEffect(() => {
    const unsub = subscribeMissionDrive((snap) => {
      setBox((prev) => (sameBox(prev, snap.highlight) ? prev : snap.highlight));
    });
    let frame = 0;
    const tick = () => {
      const current = getMissionDrive().highlight;
      if (current?.missionId && typeof document !== "undefined") {
        const el =
          document.querySelector(`[data-mission-id="${cssAttr(current.missionId)}"]`) ??
          (current.testid ? document.querySelector(`[data-testid="${cssAttr(current.testid)}"]`) : null);
        const next = highlightFromElement(el, current.label, {
          missionId: current.missionId,
          testid: current.testid,
        });
        setBox((prev) => (sameBox(prev, next) ? prev : next));
      } else {
        setBox((prev) => (sameBox(prev, current) ? prev : current));
      }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => {
      unsub();
      cancelAnimationFrame(frame);
    };
  }, []);

  useEffect(() => {
    if (!box) {
      refs.setReference(null);
      return;
    }
    refs.setReference({ getBoundingClientRect: () => boxRect(box) });
  }, [box, refs]);

  if (!box || typeof document === "undefined") return null;
  if (box.width <= 0 && box.height <= 0) return null;

  return createPortal(
    <>
      <div
        data-testid="target-highlight"
        className="pointer-events-none"
        style={{
          position: "fixed",
          top: box.top,
          left: box.left,
          width: box.width,
          height: box.height,
          outline: `2px solid ${OUTLINE}`,
          outlineOffset: 3,
          background: FILL,
          zIndex: 80,
          animation: "mission-highlight-fade 2.5s linear forwards",
        }}
      />
      <span
        ref={refs.setFloating}
        className="pointer-events-none z-[81] max-w-[220px] truncate whitespace-nowrap bg-[#111] px-1.5 py-0.5 font-mono text-[10px] tracking-[0.02em]"
        style={{ ...floatingStyles, color: MISSION_COPPER_INK }}
        data-testid="target-highlight-label"
      >
        {box.label}
      </span>
    </>,
    document.body,
  );
}

function cssAttr(value: string): string {
  if (typeof CSS !== "undefined" && typeof CSS.escape === "function") return CSS.escape(value);
  return value.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
}
